import {Spec, SpecGroup} from "@webmate-web-core/shared/types/testmgmt";
import {SpecGroupId, SpecId} from '@webmate-web-core/shared/types/ids';
import {Observable, of} from "rxjs";
import {first, mergeMap} from "rxjs/operators";
import {TestService} from "@webmate-web-core/shared/services/test.service";
import {Tag} from "@webmate-web-core/shared/types/tag";
import {WcagAnalysisResults} from '../../../workbench/workbench-plugins/analysis/views/wcag-analysis-view.component';
import {AssessmentTask} from '@webmate-web-core/shared/services/audit.service';

/**
 * Recursively (hence the odd signature) retrieve all spec groups and their parents from the server.
 */
export function retrieveSpecGroupsHelper(soFar: SpecGroup[], remainingSpecGroupIds: SpecGroupId[], testService: TestService): Observable<SpecGroup[]> {
    if (remainingSpecGroupIds.length === 0) {
        return of(soFar);
    } else {
        return testService.getSpecGroups$(remainingSpecGroupIds).pipe(mergeMap(groups => {
            const newNeededGroups = groups
                .map(g => g.parent)
                .filter((newParent): newParent is SpecGroupId => !!newParent && !soFar.find(g => g.id === newParent));

            return retrieveSpecGroupsHelper(soFar.concat(groups), newNeededGroups, testService);
        }));
    }
}

export type SpecForestNodeId = string;

export interface BackendSpecForestNode {
    node: {specGroupId: SpecGroupId} | {specId: SpecId},
    children: BackendSpecForestNode[]
}


export interface SpecForestNode {
    type: 'item' | 'group',
    id: string,
    node: SpecGroup|Spec,
    children: any[]
}


/**
 * Filter axe observations for a set of spec labels.
 */
export function filterAxeObservationsForSpec(axeResults: WcagAnalysisResults, specLabels: Tag[]): WcagAnalysisResults {
    const axeLabels = mapSpecLabelsToAxeLabels(specLabels || []);

    // Filter each property (violations, inapplicable, passes)
    return {
        violations: axeResults.violations.filter(violation =>
            violation.tags.some((tag: any) => axeLabels.includes(tag))
        ),
        inapplicable: axeResults.inapplicable.filter(inapplicable =>
            inapplicable.tags.some((tag: any) => axeLabels.includes(tag))
        ),
        passes: axeResults.passes.filter(pass =>
            pass.tags.some((tag: any) => axeLabels.includes(tag))
        )
    };
}

/**
 * Map labels on specs to to the labels created by axe core. Used to associate axe results with specs.
 */
export function mapSpecLabelsToAxeLabels(specLabels: Tag[]) {
    let result: any[] = [];
    specLabels.forEach(label => {
        if (Object.keys(label)[0] === 'wcag_level') {
            result.push('wcag' + label['wcag_level']);
        } else if (Object.keys(label)[0] === 'wcag_sc') {
            result.push('wcag' + label['wcag_sc'].replaceAll('.', ''));
        }
    });
    return result;
}

/**
 * Make a list of trees from specs and their groups.
 */
export function calculateSpecForest(specs: Spec[], specGroups: SpecGroup[], nameFilter: string = ''): SpecForestNode[] {
    const nodeMap: {[key: string]: SpecForestNode} = {};

    specs.forEach((spec: Spec) => {
        if (spec.title.toLowerCase().indexOf(nameFilter.toLowerCase()) === -1) {
            return;
        }
        nodeMap[spec.id] = {type: 'item', id: spec.id, node: spec, children: []};
    })

    specGroups.forEach(group => {
        if (group.name.toLowerCase().indexOf(nameFilter.toLowerCase()) === -1) {
            return;
        }
        nodeMap[group.id] = {type: 'group', id: group.id, node: group, children: []};
    });

    const forest: any[] = [];
    Object.entries(nodeMap)
        .sort((nodeA, nodeB) =>
            alphanumericDisplayIdSort(nodeA[1].node.displayId, nodeB[1].node.displayId))
        .forEach(([_, node]) => {
            if (node.type === 'item') {
                const group = (node.node as Spec).groupId;
                const parent = group && nodeMap[group];
                if (!parent) {
                    forest.push(node);
                } else {
                    parent.children.push(node);
                }
            } else if (node.type === 'group') {
                const parentGroup = (node.node as SpecGroup).parent;
                const parent = parentGroup && nodeMap[parentGroup];
                if (!parent) {
                    forest.push(node);
                } else {
                    parent.children.push(node);
                }
            }
        });

    return forest.sort((a, b) =>  alphanumericDisplayIdSort(a.node.displayId, b.node.displayId));
}

/**
 * Compares two `SpecForestNode` objects based on their `displayId` in alphanumeric order.
 * The `displayId` is expected to have a pattern such as `Number.Number.NumberLetter` e.g. `1.3` or `1.11.5a`.
 * This function splits the `displayId` into numeric and string parts and compares them part by part.
 * `displayId` is split by numbers to prioritize them.
 *
 * - Numeric parts are compared numerically.
 * - String parts are compared lexicographically.
 * - If the types of the parts differ (number vs. string), numbers are considered smaller than strings.
 *
 * @param {string?} a - The first displayId to compare.
 * @param {string?} b - The second displayId to compare.
 * @returns {number} - Returns:
 *  - `-1` if `a` should appear before `b`,
 *  - `1` if `a` should appear after `b`,
 *  - `0` if they are considered equal.
 */
export function alphanumericDisplayIdSort(a?: string, b?: string): number {
    // Splits displayId by Numbers e.g. 1, or 12 etc. and converts these to real numbers if possible
    const firstDisplayID = a?.split(/(\d+)/g).map(element => isNaN(Number(element)) ? element : Number(element)) || [];
    const secondDisplayID = b?.split(/(\d+)/g).map(element => isNaN(Number(element)) ? element : Number(element)) || [];

    // Compare the two transformed lists part by part
    for (let i = 0; i < Math.max(firstDisplayID.length, secondDisplayID.length); i++) {
        const partA = firstDisplayID[i];
        const partB = secondDisplayID[i];

        // Handle missing parts (if one list is shorter)
        if (partA === undefined) return -1;
        if (partB === undefined) return 1;

        // If both parts are numbers, compare them numerically
        if (typeof partA === 'number' && typeof partB === 'number') {
            if (partA < partB) return -1;
            if (partA > partB) return 1;
        }

        // If both parts are strings, compare lexicographically
        if (typeof partA === 'string' && typeof partB === 'string') {
            const result = partA.localeCompare(partB);
            if (result !== 0) return result;
        }

        // If part types differ, numbers are considered smaller than strings
        if (typeof partA === 'number' && typeof partB === 'string') return -1;
        if (typeof partA === 'string' && typeof partB === 'number') return 1;
    }

    return 0;
}

export function findNodeInSpecForest(nodes: SpecForestNode[], id: string): SpecForestNode | null {
    for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === id) {
            return nodes[i];
        }
        const found = findNodeInSpecForest(nodes[i].children, id);
        if (found) {
            return found;
        }
    }
    return null;
}

export function getDescendantNodesInSpecForest(node: SpecForestNode): SpecForestNode[] {
    return [node].concat(node.children.flatMap(getDescendantNodesInSpecForest));
}

/**
 * Delete a node from a spec forest.
 * Returns the deleted nodes.
 */
export function deleteNodeAndDescendandsInSpecForest(nodes: SpecForestNode[], id: string): SpecForestNode[] {
    let deletedNodes: SpecForestNode[] = [];
    for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === id) {
            const nodesToDelete = [nodes[i]].concat(nodes[i].children.flatMap(getDescendantNodesInSpecForest));
            nodes.splice(i, 1);
            deletedNodes = deletedNodes.concat(nodesToDelete);
        } else {
            deletedNodes = deletedNodes.concat(deleteNodeAndDescendandsInSpecForest(nodes[i].children, id));
        }
    }
    return deletedNodes;
}
